import { appState } from './appState';
const DEFAULT_LOCALE = 'en';
const STORAGE_KEY = 'blindquery_locale';
const localeContext = require.context('./locales', false, /\.json$/);
const localeLabels = {
    en: 'English',
    es: 'Español',
    de: 'Deutsch',
    fr: 'Français',
    pt: 'Português',
    it: 'Italiano',
    ca: 'Català'
};
const translationsCache = new Map();
let currentLocale = DEFAULT_LOCALE;
function normalizeLocale(locale) {
    return locale.toLowerCase();
}
function getAvailableLocaleCodes() {
    return localeContext
        .keys()
        .map(key => key.replace('./', '').replace('.json', ''))
        .sort();
}
function getLocaleMessages(locale) {
    const normalized = normalizeLocale(locale);
    const cached = translationsCache.get(normalized);
    if (cached) {
        return cached;
    }
    const lookupKey = `./${normalized}.json`;
    const availableKeys = localeContext.keys();
    if (!availableKeys.includes(lookupKey)) {
        if (normalized !== DEFAULT_LOCALE) {
            return getLocaleMessages(DEFAULT_LOCALE);
        }
        return {};
    }
    const module = localeContext(lookupKey);
    const messages = module.default ?? module;
    translationsCache.set(normalized, messages);
    return messages;
}
function resolveMessage(messages, key) {
    return key
        .split('.')
        .reduce((acc, part) => {
        if (!acc || typeof acc === 'string') {
            return undefined;
        }
        return acc[part];
    }, messages);
}
function interpolate(template, values) {
    if (!values) {
        return template;
    }
    return template.replace(/\{(\w+)\}/g, (_, token) => values[token] ?? `{${token}}`);
}
export function t(key, values) {
    const messages = getLocaleMessages(currentLocale);
    const defaultMessages = getLocaleMessages(DEFAULT_LOCALE);
    const resolved = resolveMessage(messages, key) ?? resolveMessage(defaultMessages, key) ?? key;
    return interpolate(resolved, values);
}
function getPathSegments(pathname) {
    return pathname.split('/').filter(Boolean);
}
function stripLocaleFromSegments(segments) {
    const locales = getAvailableLocaleCodes();
    if (segments.length === 0) {
        return [];
    }
    return locales.includes(segments[0]) ? segments.slice(1) : segments;
}
function buildLocalePath(locale, pathname) {
    const normalizedLocale = normalizeLocale(locale);
    const segments = stripLocaleFromSegments(getPathSegments(pathname));
    const prefix = normalizedLocale === DEFAULT_LOCALE ? [] : [normalizedLocale];
    const nextSegments = [...prefix, ...segments];
    if (nextSegments.length === 0) {
        return '/';
    }
    return `/${nextSegments.join('/')}/`;
}
function getLocaleFromPathname(pathname) {
    const segments = getPathSegments(pathname);
    const candidate = segments[0];
    const locales = getAvailableLocaleCodes();
    if (candidate && locales.includes(candidate)) {
        return candidate;
    }
    return DEFAULT_LOCALE;
}
function getStoredLocale() {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) {
        return null;
    }
    const normalized = normalizeLocale(stored);
    return getAvailableLocaleCodes().includes(normalized) ? normalized : null;
}
function setStoredLocale(locale) {
    window.localStorage.setItem(STORAGE_KEY, locale);
}
function updateHtmlLang(locale) {
    document.documentElement.lang = locale;
}
function updateLanguageSelect(locale) {
    const select = document.getElementById('languageSelect');
    if (!select) {
        return;
    }
    select.value = locale;
}
function applyTranslationAttributes() {
    document.querySelectorAll('[data-i18n]').forEach(element => {
        const key = element.dataset.i18n;
        if (key) {
            element.textContent = t(key);
        }
    });
    document.querySelectorAll('[data-i18n-placeholder]').forEach(element => {
        const key = element.dataset.i18nPlaceholder;
        if (key && 'placeholder' in element) {
            element.placeholder = t(key);
        }
    });
    document.querySelectorAll('[data-i18n-title]').forEach(element => {
        const key = element.dataset.i18nTitle;
        if (key) {
            element.setAttribute('title', t(key));
        }
    });
    document.querySelectorAll('[data-i18n-aria-label]').forEach(element => {
        const key = element.dataset.i18nAriaLabel;
        if (key) {
            element.setAttribute('aria-label', t(key));
        }
    });
}
function applySeoTranslations() {
    document.querySelectorAll('[data-i18n-content]').forEach(element => {
        const key = element.dataset.i18nContent;
        if (key && element instanceof HTMLMetaElement) {
            element.content = t(key);
        }
    });
    document.querySelectorAll('[data-i18n-href]').forEach(element => {
        const key = element.dataset.i18nHref;
        if (key && element instanceof HTMLLinkElement) {
            element.href = t(key);
        }
    });
}
function getBasePath(pathname) {
    const segments = stripLocaleFromSegments(getPathSegments(pathname));
    if (segments.length === 0) {
        return '/';
    }
    return `/${segments.join('/')}/`;
}
function buildCanonicalUrl(locale) {
    const path = buildLocalePath(locale, getBasePath(window.location.pathname));
    return `${window.location.origin}${path}`;
}
function syncAlternateLinks() {
    const head = document.head;
    head.querySelectorAll('link[data-generated-alternate="true"]').forEach(element => element.remove());
    const basePath = getBasePath(window.location.pathname);
    const locales = getAvailableLocaleCodes();
    locales.forEach(locale => {
        const link = document.createElement('link');
        link.rel = 'alternate';
        link.hreflang = locale;
        link.href = `${window.location.origin}${buildLocalePath(locale, basePath)}`;
        link.dataset.generatedAlternate = 'true';
        head.appendChild(link);
    });
    const defaultLink = document.createElement('link');
    defaultLink.rel = 'alternate';
    defaultLink.hreflang = 'x-default';
    defaultLink.href = `${window.location.origin}${buildLocalePath(DEFAULT_LOCALE, basePath)}`;
    defaultLink.dataset.generatedAlternate = 'true';
    head.appendChild(defaultLink);
}
function applySeoUrls() {
    const canonicalUrl = buildCanonicalUrl(currentLocale);
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
        canonicalLink.href = canonicalUrl;
    }
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
        ogUrl.content = canonicalUrl;
    }
    syncAlternateLinks();
}
export function applyTranslations() {
    applyTranslationAttributes();
    applySeoTranslations();
    updateHtmlLang(currentLocale);
    updateLanguageSelect(currentLocale);
    appState.defaultInputPlaceholder = t('compose.placeholder');
    applySeoUrls();
}
export function setSeoState(isLoggedIn) {
    const titleElement = document.querySelector('title');
    if (titleElement) {
        titleElement.textContent = t('seo.title');
    }
    if (isLoggedIn) {
        document.querySelectorAll('[data-seo="guest"]').forEach(element => {
            if (element instanceof HTMLMetaElement) {
                element.content = '';
            }
            else if (element instanceof HTMLLinkElement) {
                element.href = '';
            }
        });
        return;
    }
    applySeoTranslations();
    applySeoUrls();
}
export function getAvailableLocales() {
    return getAvailableLocaleCodes().map(code => ({
        code,
        label: localeLabels[code] ?? code.toUpperCase()
    }));
}
export function getCurrentLocale() {
    return currentLocale;
}
export function initializeI18n() {
    // First, check if the URL contains a locale path (e.g., /it/, /es/)
    const urlPathname = window.location.pathname;
    const pathLocale = getLocaleFromPathname(urlPathname);
    // Also check the actual URL href to see if there's a locale in it
    const hrefMatch = window.location.href.match(/localhost:3000\/([a-z]{2})\//i);
    const hrefLocale = hrefMatch ? hrefMatch[1].toLowerCase() : null;
    // Use href locale if pathname locale is default but href has a locale
    const effectiveLocale = (pathLocale === DEFAULT_LOCALE && hrefLocale) ? hrefLocale : pathLocale;
    const storedLocale = getStoredLocale();
    const shouldUseStored = Boolean(storedLocale && effectiveLocale === DEFAULT_LOCALE && storedLocale !== DEFAULT_LOCALE);
    const initialLocale = shouldUseStored && storedLocale ? storedLocale : effectiveLocale;
    setLocale(initialLocale, {
        updateHistory: shouldUseStored,
        persist: true,
        emitEvent: false
    });
    window.addEventListener('popstate', () => {
        const nextLocale = getLocaleFromPathname(window.location.pathname);
        if (nextLocale !== currentLocale) {
            setLocale(nextLocale, { updateHistory: false, persist: true, emitEvent: true });
        }
    });
}
export function initializeLanguageSelect() {
    const select = document.getElementById('languageSelect');
    if (!select) {
        return;
    }
    select.innerHTML = '';
    getAvailableLocales().forEach(locale => {
        const option = document.createElement('option');
        option.value = locale.code;
        option.textContent = locale.label;
        select.appendChild(option);
    });
    select.value = currentLocale;
    select.addEventListener('change', () => {
        setLocale(select.value, { updateHistory: true, persist: true, emitEvent: true });
    });
}
export function setLocale(locale, options = {}) {
    const normalized = normalizeLocale(locale);
    const availableLocales = getAvailableLocaleCodes();
    const nextLocale = availableLocales.includes(normalized) ? normalized : DEFAULT_LOCALE;
    currentLocale = nextLocale;
    if (options.persist !== false) {
        setStoredLocale(nextLocale);
    }
    if (options.updateHistory !== false) {
        const nextPath = buildLocalePath(nextLocale, window.location.pathname);
        window.history.pushState({}, '', nextPath);
    }
    applyTranslations();
    if (options.emitEvent !== false) {
        document.dispatchEvent(new CustomEvent('bq:locale-change', { detail: nextLocale }));
    }
}
